import { useStableCallback } from "@prismicio/editor-support/React"
import {
  contentEditableClass,
  contentEditableMultilinePlaceholderClass,
  Field,
  FieldControl,
  FieldLabel,
  useEditor
} from "@prismicio/editor-ui"
import type { RichTextContent } from "@prismicio/types-internal/lib/content"
import type {
  RichText,
  RichTextNodeType
} from "@prismicio/types-internal/lib/customtypes"
import { type Extensions, getSchema } from "@tiptap/core"
import { History } from "@tiptap/extension-history"
import { EditorContent } from "@tiptap/react"
import type { Schema } from "prosemirror-model"
import { useMemo } from "react"

import { getFieldLabel } from "../utils"
import { BubbleMenu } from "./BubbleMenu"
import { CoreComponents, Document } from "./coreExtensions"
import { TextDirection } from "./globalExtensions"
import {
  ExtensionType,
  type NodeExtension,
  NodeType,
  type WidgetExtension
} from "./models"
import { composeExtensions } from "./models/RichTextConfig"

interface Props {
  id: string
  content: RichTextContent | undefined
  field: RichText
  readOnly: boolean
  onContentChange: (content: RichTextContent | undefined) => void
}

export function RichTextField(props: Props) {
  const { content, field, id, readOnly } = props

  const config = useConfig(field)

  const prosemirrorContent =
    content &&
    Document.converter(config.nodeExtensions).fromPrismic(
      config.schema,
      config.nodeTypes,
      content
    )

  const onContentChange = useStableCallback(props.onContentChange)

  const editor = useEditor(
    {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      content: prosemirrorContent?.toJSON(),
      editable: !readOnly,
      editorProps: {
        attributes: {
          class: `${contentEditableClass} ${contentEditableMultilinePlaceholderClass}`,
          id,
          tabIndex: "0"
        }
      },
      extensions: config.allComponents,
      injectCSS: false,
      onUpdate({ editor }) {
        if (editor.isEmpty) return onContentChange(undefined)

        const prismic = Document.converter(config.nodeExtensions).toPrismic(
          editor.state.doc
        )
        onContentChange(prismic)
      }
    },
    [config, readOnly]
  )

  /**
   * Note that with Tiptap 2.3.0, BubbleMenu MUST be declared before EditorContent 😅
   * https://github.com/ueberdosis/tiptap/issues/4619#issuecomment-1869042861
   */
  return (
    <Field>
      <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
      <FieldControl>
        {editor.isEditable && (
          <BubbleMenu
            editor={editor}
            nodeTypes={config.names.concat(TextDirection.richTextNodeType)}
            labels={config.labels}
          />
        )}
        <EditorContent editor={editor} style={{ flexGrow: 1 }} />
      </FieldControl>
    </Field>
  )
}

function useConfig(field: RichText): Config {
  // Tiptap's `useEditor` doesn't allow to dynamically change the list of extensions.
  // To fix this, trigger a re-mount with this key by re-computing the useMemo.
  const extensionsKey = `${field.config?.multi ?? ""}-${
    field.config?.single ?? ""
  }`

  return useMemo(() => {
    const { extensions, nodeTypes } = composeExtensions(field.config)

    const nodeExtensions = extensions.filter(extensionIsTypeNode)

    const names = extensions.flatMap((e) => e.richTextNodeTypes)
    const components = extensions.map((e) => e.component)

    const supportedExtensionNames = extensions
      .filter(extensionIsSupported)
      .map((extension) => extension.name)

    const allComponents = [
      ...components,
      ...CoreComponents(field, nodeExtensions),
      TextDirection.component.configure({ supportedExtensionNames }),
      History
    ]

    const extensionNames = allComponents.map((c) => c.name)
    const schema = getSchema(allComponents)
    const labels = getLabels(field.config?.labels)

    return {
      allComponents,
      extensionNames,
      nodeTypes,
      nodeExtensions,
      names,
      schema,
      labels
    }
    // We don't want to recompute this config on every render and field is unstable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionsKey])
}

function extensionIsTypeNode(value: WidgetExtension): value is NodeExtension {
  return value.extType === ExtensionType.Node
}

function extensionIsSupported(extension: WidgetExtension) {
  if (extension.extType !== ExtensionType.Node) return false
  if (extension.nodeType !== NodeType.Text) return false
  if (extension.lockedFormat) return false
  return true
}

function getLabels(labels: readonly string[] | undefined) {
  // Convert prismic model readonly array to a standard array of labels
  return Array.from(labels ?? [])
}

interface Config {
  allComponents: Extensions
  extensionNames: string[]
  nodeTypes: Set<RichTextNodeType>
  nodeExtensions: NodeExtension[]
  names: RichTextNodeType[]
  schema: Schema
  labels: string[]
}
